import type { Session } from "@supabase/supabase-js";

import { getSupabase } from "./supabase";

/**
 * MER-197: client-side helpers for staff impersonation.
 *
 * Flow:
 *   1. startImpersonation(tenantId) calls the security-definer
 *      `impersonation_start` RPC, then forces a session refresh so the
 *      new app_metadata.view_as_tenant_id claim lands in the JWT.
 *   2. stopImpersonation() calls the `impersonation_stop` RPC, then
 *      refreshes to clear the claim from the JWT.
 *   3. readImpersonationClaim(session) lets components read the claim
 *      from React state without an extra round-trip.
 *
 * Security model: the RPCs are the trust boundary, not this file. A
 * malicious caller skipping these helpers and calling the RPC directly
 * still hits `is_staff()` and the audit insert. The 15-minute TTL is
 * enforced inside `effective_tenant_filter()` so a stale claim never
 * extends impersonation past its window.
 */

export interface ImpersonationStartResult {
  tenantId: string;
  slug: string;
  name: string;
  expiresAt: string;
}

export interface ImpersonationClaim {
  viewAsTenantId: string;
  startedAt: string | null;
  expiresAt: string | null;
}

/**
 * Reads the current impersonation claim off the session JWT. Returns
 * null when not impersonating. Callers should re-run this on every
 * session change since the claim only refreshes when the JWT does.
 */
export function readImpersonationClaim(
  session: Session | null | undefined,
): ImpersonationClaim | null {
  if (!session?.user) return null;
  const meta = session.user.app_metadata as Record<string, unknown> | undefined;
  if (!meta) return null;
  const viewAs = meta.view_as_tenant_id;
  if (typeof viewAs !== "string" || viewAs.length === 0) return null;
  const expiresAt =
    typeof meta.impersonation_expires_at === "string" ? meta.impersonation_expires_at : null;
  // Defense-in-depth: client-side TTL guard. RLS also enforces the
  // window, but treating the claim as inactive past its deadline
  // keeps the banner from lying about scoping.
  if (expiresAt) {
    const deadline = Date.parse(expiresAt);
    if (Number.isFinite(deadline) && deadline <= Date.now()) return null;
  }
  return {
    viewAsTenantId: viewAs,
    startedAt:
      typeof meta.impersonation_started_at === "string" ? meta.impersonation_started_at : null,
    expiresAt,
  };
}

export async function startImpersonation(tenantId: string): Promise<ImpersonationStartResult> {
  const supabase = getSupabase();
  const { data, error } = await supabase.rpc("impersonation_start", {
    p_tenant_id: tenantId,
  });
  if (error) {
    throw new Error(`impersonation_start failed: ${error.message}`);
  }
  // Pull the new claim into the access token. Without this the next
  // PostgREST request still carries the pre-impersonation JWT and
  // sees cross-tenant data through is_staff() instead of being
  // scoped to view_as_tenant_id.
  const refreshed = await supabase.auth.refreshSession();
  if (refreshed.error) {
    throw new Error(`session refresh after impersonate failed: ${refreshed.error.message}`);
  }
  return parseStartResult(data);
}

export async function stopImpersonation(): Promise<void> {
  const supabase = getSupabase();
  const { error } = await supabase.rpc("impersonation_stop");
  if (error) {
    throw new Error(`impersonation_stop failed: ${error.message}`);
  }
  const refreshed = await supabase.auth.refreshSession();
  if (refreshed.error) {
    throw new Error(`session refresh after stop failed: ${refreshed.error.message}`);
  }
}

function parseStartResult(raw: unknown): ImpersonationStartResult {
  if (!raw || typeof raw !== "object") {
    throw new Error("impersonation_start returned unexpected payload");
  }
  const obj = raw as Record<string, unknown>;
  const tenantId = typeof obj.tenant_id === "string" ? obj.tenant_id : null;
  const slug = typeof obj.slug === "string" ? obj.slug : null;
  const name = typeof obj.name === "string" ? obj.name : null;
  const expiresAt = typeof obj.expires_at === "string" ? obj.expires_at : null;
  if (!tenantId || !slug || !name || !expiresAt) {
    throw new Error("impersonation_start payload missing required fields");
  }
  return { tenantId, slug, name, expiresAt };
}
